// MIT License - Copyright (c) 2026 Doug McNall - See .kiro/steering/general/development-standards.md for full license

/**
 * WeaponsContext - Centralized Weapons State Management
 * Replaces global WeaponsManager singleton with React Context
 */

import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import GameConfig from '../config/GameConfig';
import STORAGE_KEYS from '../utils/system/STORAGE_KEYS';

const WeaponsContext = createContext(null);

// Initialize weapons from config
const createInitialWeapons = () => {
  const slots = [];
  for (let i = 0; i < GameConfig.weapons.maxWeaponSlots; i++) {
    if (i === 0) {
      // First slot gets starting weapon
      slots.push({
        id: GameConfig.weapons.startingWeapon.id,
        slot: i + 1,
        name: GameConfig.weapons.startingWeapon.name,
        type: 'LASER',
        damage: GameConfig.weapons.startingWeapon.damage,
        ammo: GameConfig.weapons.maxCharge,
        maxAmmo: GameConfig.weapons.maxCharge,
        cost: GameConfig.weapons.startingWeapon.cost,
        installed: true,
      });
    } else {
      // Empty slots
      slots.push({
        id: `weapon_${i + 1}`,
        slot: i + 1,
        name: 'EMPTY',
        type: null,
        damage: 0,
        ammo: 0,
        maxAmmo: 0,
        cost: 0,
        installed: false,
      });
    }
  }
  return slots;
};

export function WeaponsProvider({ children }) {
  // Initialize from localStorage or defaults
  const [weapons, setWeapons] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEYS.WEAPONS);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        // Validate structure - ensure it's an array with expected properties
        if (Array.isArray(parsed) && parsed.length > 0) {
          // Check if this is old format (has 'charge' instead of 'ammo')
          if (parsed[0].hasOwnProperty('charge') && !parsed[0].hasOwnProperty('ammo')) {
            console.warn('[WeaponsContext] Migrating old weapon format (charge -> ammo)');
            // Migrate old format to new format
            const migrated = parsed.map(w => ({
              ...w,
              ammo: w.charge || 0,
              maxAmmo: w.maxCharge || 100,
              // Remove old properties
              charge: undefined,
              maxCharge: undefined
            }));
            localStorage.setItem(STORAGE_KEYS.WEAPONS,JSON.stringify(migrated));
            return migrated;
          }
          // Check if this is new format
          else if (parsed[0].hasOwnProperty('installed') && parsed[0].hasOwnProperty('ammo')) {
            return parsed;
          }
          // Invalid structure
          else {
            console.warn('[WeaponsContext] Invalid weapons structure in localStorage, resetting');
            localStorage.removeItem(STORAGE_KEYS.WEAPONS);
          }
        }
      } catch (e) {
        console.error('[WeaponsContext] Failed to parse saved weapons:', e);
        localStorage.removeItem(STORAGE_KEYS.WEAPONS);
      }
    }
    return createInitialWeapons();
  });

  const [unlockedSlots, setUnlockedSlots] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEYS.UNLOCKED_WEAPON_SLOTS);
    return saved !== null ? parseInt(saved, 10) : GameConfig.weapons.startingSlots;
  });

  // Persist to localStorage
  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.WEAPONS,JSON.stringify(weapons));
  }, [weapons]);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.UNLOCKED_WEAPON_SLOTS, unlockedSlots.toString());
  }, [unlockedSlots]);

  // Get all weapons
  const getWeapons = useCallback(() => {
    return weapons;
  }, [weapons]);

  // Get unlocked slots count
  const getUnlockedSlots = useCallback(() => {
    return unlockedSlots;
  }, [unlockedSlots]);

  // Get available weapons for a location
  const getAvailableWeapons = useCallback((locationName) => {
    // Get weapon IDs available at this location type
    const locationWeaponIds = GameConfig.weapons.weaponInventory[locationName] || [];
    
    // Map IDs to full weapon data from catalog
    const availableWeapons = locationWeaponIds
      .map(id => GameConfig.weapons.weaponsCatalog[id])
      .filter(w => w !== undefined); // Filter out any undefined weapons
    
    // Filter out weapons already purchased
    const installedWeaponIds = weapons.filter(w => w.installed).map(w => w.id);
    return availableWeapons.filter(w => !installedWeaponIds.includes(w.id));
  }, [weapons]);

  // Install weapon in slot
  const installWeapon = useCallback((slotIndex, weaponData) => {
    const maxAmmo = GameConfig.weapons.maxCharge;
    setWeapons(prev => prev.map((w, idx) => {
      if (idx === slotIndex) {
        return {
          ...w,
          id: weaponData.id,
          name: weaponData.name,
          type: weaponData.id, // Use weapon ID as type
          damage: weaponData.damage,
          ammo: maxAmmo,
          maxAmmo: maxAmmo,
          cost: weaponData.cost,
          installed: true,
        };
      }
      return w;
    }));
  }, []);

  // Deplete weapon ammo
  const depleteWeapon = useCallback((slotIndex, amount) => {
    setWeapons(prev => prev.map((w, idx) => {
      if (idx === slotIndex && w.installed) {
        const newAmmo = Math.max(0, w.ammo - amount);
        return { ...w, ammo: newAmmo };
      }
      return w;
    }));
  }, []);

  // Reload weapon
  const reloadWeapon = useCallback((slotIndex) => {
    setWeapons(prev => prev.map((w, idx) => {
      if (idx === slotIndex && w.installed) {
        return { ...w, ammo: w.maxAmmo };
      }
      return w;
    }));
    return { success: true };
  }, []);

  // Reload all weapons
  const reloadAll = useCallback(() => {
    setWeapons(prev => prev.map(w => {
      if (w.installed) {
        return { ...w, ammo: w.maxAmmo };
      }
      return w;
    }));
    return { success: true };
  }, []);

  // Unlock next weapon slot
  const unlockSlot = useCallback(() => {
    if (unlockedSlots >= GameConfig.weapons.maxWeaponSlots) {
      return { success: false, error: 'All slots unlocked' };
    }
    setUnlockedSlots(prev => prev + 1);
    return { success: true, newSlotCount: unlockedSlots + 1 };
  }, [unlockedSlots]);

  // Get next slot unlock cost
  const getNextSlotCost = useCallback(() => {
    if (unlockedSlots >= GameConfig.weapons.maxWeaponSlots) {
      return null;
    }
    // Map slot number to cost key (slot 2, 3, 4)
    const slotKey = `slot${unlockedSlots + 1}`;
    return GameConfig.weapons.slotUnlockCosts[slotKey];
  }, [unlockedSlots]);

  // Check if can purchase weapon
  const canPurchaseWeapon = useCallback((weaponId) => {
    const weaponData = GameConfig.weapons.weaponsCatalog[weaponId];
    if (!weaponData) {
      return { canPurchase: false, reason: 'Weapon not found' };
    }

    const emptySlots = weapons.slice(0, unlockedSlots).filter(w => !w.installed);
    if (emptySlots.length === 0) {
      return { canPurchase: false, reason: 'No empty slots' };
    }

    return { canPurchase: true, weapon: weaponData };
  }, [weapons, unlockedSlots]);

  // Purchase and install weapon
  const purchaseWeapon = useCallback((weaponId, slotIndex) => {
    const weaponData = GameConfig.weapons.weaponsCatalog[weaponId];
    if (!weaponData) {
      return { success: false, error: 'Weapon not found' };
    }

    if (slotIndex >= unlockedSlots) {
      return { success: false, error: 'Slot not unlocked' };
    }

    installWeapon(slotIndex, weaponData);
    return { success: true, weapon: weaponData };
  }, [unlockedSlots, installWeapon]);

  // Reset to defaults (for game reset)
  const resetWeapons = useCallback(() => {
    setWeapons(createInitialWeapons());
    setUnlockedSlots(GameConfig.weapons.startingSlots);
    localStorage.removeItem(STORAGE_KEYS.WEAPONS);
    localStorage.removeItem(STORAGE_KEYS.UNLOCKED_WEAPON_SLOTS);
  }, []);

  // Sell weapon (get back 50% of original cost)
  const sellWeapon = useCallback((slotIndex) => {
    const weapon = weapons[slotIndex];
    if (!weapon || !weapon.installed) {
      return { success: false, error: 'Cannot sell this weapon' };
    }

    // Check if this is the last weapon - prevent selling if so
    const installedWeaponsCount = weapons.filter(w => w.installed).length;
    if (installedWeaponsCount <= 1) {
      return { success: false, error: 'Cannot sell last weapon - you must have at least one weapon equipped' };
    }

    const sellPrice = Math.floor(weapon.cost * 0.5); // 50% of original cost
    
    // Clear the weapon slot
    setWeapons(prev => prev.map((w, idx) => {
      if (idx === slotIndex) {
        return {
          id: `weapon_${idx + 1}`,
          slot: idx + 1,
          name: 'EMPTY',
          type: null,
          damage: 0,
          ammo: 0,
          maxAmmo: 0,
          cost: 0,
          installed: false,
        };
      }
      return w;
    }));

    return { 
      success: true, 
      sellPrice,
      weaponName: weapon.name 
    };
  }, [weapons]);

  const value = {
    weapons,
    unlockedSlots,
    getWeapons,
    getUnlockedSlots,
    getAvailableWeapons,
    installWeapon,
    depleteWeapon,
    reloadWeapon,
    reloadAll,
    unlockSlot,
    getNextSlotCost,
    canPurchaseWeapon,
    purchaseWeapon,
    sellWeapon,
    resetWeapons,
  };

  // Debug: Log when weapons change
  useEffect(() => {
    // Removed verbose logging for production
  }, [weapons]);

  return (
    <WeaponsContext.Provider value={value}>
      {children}
    </WeaponsContext.Provider>
  );
}

// Custom hook to use weapons context
export function useWeapons() {
  const context = useContext(WeaponsContext);
  if (!context) {
    throw new Error('useWeapons must be used within a WeaponsProvider');
  }
  return context;
}

export default WeaponsContext;
